/**
 * HospitalProfileDialog
 * ---------------------
 * Read-only profile card that surfaces everything a hospital provided at
 * signup, plus a "Promote to Health System" form to expand a single-
 * facility account into a multi-facility group without re-entering data.
 *
 * Mounted from `AccountDetailModal` in CrmManager — receives `accountId`,
 * which it hits against /api/admin/crm/{accountId}/hospital-profile. Hidden
 * by default; opens via the "Hospital Profile" button in the modal header.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  X,
  Building2,
  User,
  MapPin,
  Mail,
  Phone,
  Briefcase,
  Stethoscope,
  Loader2,
  ShieldCheck,
  Plus,
  ChevronRight,
  ShoppingCart,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-start justify-between gap-3 py-1.5">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 shrink-0 w-32">{label}</span>
    <span className={`text-sm text-slate-900 text-right break-words flex-1 ${mono ? "font-mono" : ""}`}>
      {value || <span className="text-slate-400 italic">—</span>}
    </span>
  </div>
);

const Section = ({ icon: Icon, title, subtitle, children, color = "cyan" }) => {
  const palettes = {
    cyan: "bg-cyan-100 text-cyan-700 ring-cyan-200",
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
    emerald: "bg-emerald-100 text-emerald-700 ring-emerald-200",
    violet: "bg-violet-100 text-violet-700 ring-violet-200",
  };
  return (
    <section className="rounded-xl border border-slate-200 p-4 bg-white">
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-9 h-9 rounded-lg ring-1 flex items-center justify-center ${palettes[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h4 className="font-bold text-slate-900 text-sm leading-tight">{title}</h4>
          {subtitle && <p className="text-[11px] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="divide-y divide-slate-100">{children}</div>
    </section>
  );
};

export default function HospitalProfileDialog({ open, onClose, accountId, headers, onPromoted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [showPromote, setShowPromote] = useState(false);
  const [promoteForm, setPromoteForm] = useState({
    health_system_name_override: "",
    additional_facility_name: "",
    additional_facility_type: "hospital",
    additional_facility_city: "",
    additional_facility_state: "",
  });

  const load = useCallback(async () => {
    if (!open || !accountId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/crm/${accountId}/hospital-profile`, { headers });
      setData(r.data);
      // Pre-fill the rename input with the current health-system name
      setPromoteForm((f) => ({
        ...f,
        health_system_name_override: (r.data?.health_system?.name) || r.data?.account_name || "",
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load hospital profile");
    } finally {
      setLoading(false);
    }
  }, [open, accountId, headers]);

  useEffect(() => { load(); }, [load]);

  const promote = async () => {
    if (!promoteForm.additional_facility_name && !promoteForm.health_system_name_override) {
      toast.error("Add a facility name or set a parent health-system name to promote");
      return;
    }
    setPromoting(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/crm/${accountId}/promote-to-health-system`,
        promoteForm,
        { headers },
      );
      toast.success(r.data?.added_facility
        ? `Added "${r.data.added_facility.name}" to ${r.data.health_system.name}`
        : `Renamed parent → ${r.data.health_system.name}`);
      setShowPromote(false);
      setPromoteForm((f) => ({ ...f, additional_facility_name: "", additional_facility_city: "", additional_facility_state: "" }));
      load();
      onPromoted?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setPromoting(false);
    }
  };

  if (!open) return null;

  const reg = data?.registration || {};
  const acct = data?.hospital_account || {};
  const hs = data?.health_system;
  const user = data?.primary_user;
  const profile = { ...reg, ...acct };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="hospital-profile-dialog">
      <div className="bg-slate-50 rounded-2xl w-full max-w-3xl my-10 shadow-2xl">
        <div className="bg-white rounded-t-2xl border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900 leading-tight">Hospital Signup Profile</h3>
            <p className="text-xs text-slate-500 mt-0.5">{data?.account_name || "—"}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 rounded-lg p-1.5 hover:bg-slate-100"
            data-testid="hospital-profile-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading profile…
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {!profile.hospital_name && !user && (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500" data-testid="hospital-profile-empty">
                <Building2 className="w-9 h-9 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No signup data on file for this account.</p>
                <p className="text-[11px] text-slate-400 mt-1">
                  Hospitals self-register at <code>/hospital/self-register</code> — once they do, that data shows up here.
                  You can still promote this account into a Health System below using the CRM name.
                </p>
              </div>
            )}

            {(profile.hospital_name || user) && (
              <>
                {/* Quick summary strip */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Terms</p>
                    <p className="text-sm font-bold text-slate-900">{profile.payment_terms_preference || profile.payment_terms || "—"}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Beds</p>
                    <p className="text-sm font-bold text-slate-900">{profile.bed_count ?? "—"}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Orders</p>
                    <p className="text-sm font-bold text-slate-900 flex items-center gap-1"><ShoppingCart className="w-3 h-3" />{data?.orders_count || 0}</p>
                  </div>
                  <div className="rounded-lg bg-white border border-slate-200 p-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Pending</p>
                    <p className={`text-sm font-bold ${(data?.pending_orders || 0) > 0 ? "text-amber-700" : "text-slate-900"}`}>{data?.pending_orders || 0}</p>
                  </div>
                </div>

                <Section icon={Building2} title="Facility" color="cyan">
                  <Row label="Name" value={profile.hospital_name || data?.account_name} />
                  <Row label="Type" value={profile.facility_type} />
                  <Row label="NPI" value={profile.npi} mono />
                  <Row label="Bed count" value={profile.bed_count} />
                  <Row label="Address" value={profile.address} />
                  <Row label="City / State / ZIP" value={[profile.city, profile.state, profile.zip_code].filter(Boolean).join(", ")} />
                </Section>

                <Section icon={User} title="Primary Contact" subtitle="From the signup form" color="amber">
                  <Row label="Name" value={profile.contact_name || user?.name} />
                  <Row label="Title" value={profile.title || user?.title} />
                  <Row label="Department" value={profile.department || user?.department} />
                  <Row label="Email" value={profile.email || user?.email} />
                  <Row label="Phone" value={profile.phone || user?.phone} />
                </Section>

                <Section icon={ShieldCheck} title="Account Status" color="emerald">
                  <Row label="Approved" value={acct.is_approved ? "Yes" : (reg.status === "approved" ? "Yes" : "No")} />
                  <Row label="Approved at" value={acct.approved_at || reg.reviewed_at} />
                  <Row label="Registration date" value={reg.created_at} />
                  <Row label="Source" value={reg.source === "auto_from_order" ? "Auto-drafted from order" : "Self-signup"} />
                  <Row label="Health system ID" value={hs?.id || "—"} mono />
                  <Row label="Facilities on record" value={(hs?.facilities || []).length} />
                </Section>
              </>
            )}

            {hs?.facilities?.length > 0 && (
              <Section icon={Stethoscope} title={`Facilities (${hs.facilities.length})`} color="violet">
                <ul className="space-y-1.5 mt-1">
                  {hs.facilities.map((f) => (
                    <li key={f.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800"><strong>{f.name}</strong> <span className="text-slate-500">· {f.type}</span></span>
                      <span className="text-[11px] text-slate-500">{[f.city, f.state].filter(Boolean).join(", ")}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Promote to Health System — always available, even when no
                signup data exists yet, so legacy accounts can still be
                promoted into a multi-facility group using the CRM name. */}
            <section className="rounded-xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 p-4" data-testid="promote-section">
              {!showPromote ? (
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm flex items-center gap-2">
                      <Plus className="w-4 h-4 text-emerald-600" />
                      Promote to Health System
                    </h4>
                    <p className="text-xs text-slate-600 mt-1 max-w-md">
                      Expand this account into a multi-facility health system. Adds new facilities under the same parent without re-entering NPI, contact, or payment terms.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPromote(true)}
                    className="px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5 shadow-sm shrink-0"
                    data-testid="promote-open"
                  >
                    Add a facility <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <h4 className="font-bold text-slate-900 text-sm">Add a facility under this parent</h4>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Parent Health-System Name</label>
                    <input
                      type="text"
                      value={promoteForm.health_system_name_override}
                      onChange={(e) => setPromoteForm({ ...promoteForm, health_system_name_override: e.target.value })}
                      placeholder="e.g. Memorial Hermann Health System"
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      data-testid="promote-parent-name"
                    />
                    <p className="text-[10px] text-slate-500 mt-0.5">Rename if &quot;{data?.account_name}&quot; is just one facility under a bigger parent.</p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">New facility name *</label>
                      <input
                        type="text"
                        value={promoteForm.additional_facility_name}
                        onChange={(e) => setPromoteForm({ ...promoteForm, additional_facility_name: e.target.value })}
                        placeholder="Memorial Hermann Memorial City"
                        className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="promote-facility-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Type</label>
                      <select
                        value={promoteForm.additional_facility_type}
                        onChange={(e) => setPromoteForm({ ...promoteForm, additional_facility_type: e.target.value })}
                        className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="promote-facility-type"
                      >
                        <option value="hospital">Hospital</option>
                        <option value="surgery_center">Surgery Center</option>
                        <option value="dialysis">Dialysis Center</option>
                        <option value="clinic">Outpatient Clinic</option>
                        <option value="ltc">Long-Term Care</option>
                        <option value="specialty">Specialty Practice</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">City</label>
                      <input
                        type="text"
                        value={promoteForm.additional_facility_city}
                        onChange={(e) => setPromoteForm({ ...promoteForm, additional_facility_city: e.target.value })}
                        className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="promote-facility-city"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">State</label>
                      <input
                        type="text"
                        maxLength={2}
                        value={promoteForm.additional_facility_state}
                        onChange={(e) => setPromoteForm({ ...promoteForm, additional_facility_state: e.target.value.toUpperCase().slice(0, 2) })}
                        className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid="promote-facility-state"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button
                      type="button"
                      onClick={promote}
                      disabled={promoting}
                      className="px-3 py-1.5 text-xs font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 inline-flex items-center gap-1.5"
                      data-testid="promote-confirm"
                    >
                      {promoting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Promote &amp; Add Facility
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPromote(false)}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
